import { useCallback, useEffect, useRef, useState } from "react";

const LS_KEY_QUEUE = "lw_queue_v1";
const LS_KEY_INDEX = "lw_current_idx_v1";
const LS_KEY_SHUFFLE = "lw_shuffle_v1";
const LS_KEY_VOLUME = "lw_volume_v1";

function safeGet(k, fallback) {
  try {
    const v = localStorage.getItem(k);
    return v == null ? fallback : v;
  } catch { return fallback; }
}
function safeSet(k, v) {
  try { localStorage.setItem(k, v); } catch { /* ignored */ }
}

export function useAudioPlayer() {
  // Hydrate from localStorage
  const initQueue = (() => {
    try {
      const raw = safeGet(LS_KEY_QUEUE, "[]");
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  })();
  const initIndex = (() => {
    try {
      const idx = parseInt(safeGet(LS_KEY_INDEX, "-1"), 10);
      if (Number.isFinite(idx) && idx >= 0 && idx < initQueue.length) return idx;
      return -1;
    } catch { return -1; }
  })();
  const initShuffle = safeGet(LS_KEY_SHUFFLE, "0") === "1";
  const initVolume = (() => {
    const v = parseFloat(safeGet(LS_KEY_VOLUME, "0.7"));
    return Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 0.7;
  })();

  const [queue, setQueue] = useState(initQueue);
  const [currentIndex, setCurrentIndex] = useState(initIndex);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(initVolume);
  const [shuffle, setShuffle] = useState(initShuffle);

  const audioRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const freqDataRef = useRef(null);
  const analyserAvailableRef = useRef(false);
  const queueRef = useRef(queue);
  const currentIndexRef = useRef(currentIndex);
  const shuffleRef = useRef(shuffle);
  const hasUserGestureRef = useRef(false);

  useEffect(() => { queueRef.current = queue; }, [queue]);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
  useEffect(() => { shuffleRef.current = shuffle; }, [shuffle]);

  const currentTrack = currentIndex >= 0 && currentIndex < queue.length ? queue[currentIndex] : null;

  // Initialize audio element once
  useEffect(() => {
    const audio = new Audio();
    audio.crossOrigin = "anonymous";
    audio.preload = "auto";
    audio.volume = volume;
    audioRef.current = audio;

    const onTime = () => {
      setCurrentTime(audio.currentTime || 0);
      if (audio.duration && !isNaN(audio.duration)) setDuration(audio.duration);
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      const q = queueRef.current;
      const idx = currentIndexRef.current;
      if (q.length === 0) return;
      let nextIdx;
      if (shuffleRef.current && q.length > 1) {
        do { nextIdx = Math.floor(Math.random() * q.length); } while (nextIdx === idx);
      } else {
        nextIdx = (idx + 1) % q.length;
      }
      setCurrentIndex(nextIdx);
    };
    const onLoaded = () => {
      if (audio.duration && !isNaN(audio.duration)) setDuration(audio.duration);
    };

    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("loadedmetadata", onLoaded);

    // Hydrate src but don't autoplay (no user gesture yet)
    if (initIndex >= 0 && initQueue[initIndex]?.preview_url) {
      audio.src = initQueue[initIndex].preview_url;
      audio.load();
    }

    return () => {
      audio.pause();
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("loadedmetadata", onLoaded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist to localStorage
  useEffect(() => { safeSet(LS_KEY_QUEUE, JSON.stringify(queue)); }, [queue]);
  useEffect(() => { safeSet(LS_KEY_INDEX, String(currentIndex)); }, [currentIndex]);
  useEffect(() => { safeSet(LS_KEY_SHUFFLE, shuffle ? "1" : "0"); }, [shuffle]);
  useEffect(() => {
    safeSet(LS_KEY_VOLUME, String(volume));
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const initAnalyser = useCallback(() => {
    if (audioCtxRef.current) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const source = ctx.createMediaElementSource(audioRef.current);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 128;
      source.connect(analyser);
      analyser.connect(ctx.destination);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      sourceRef.current = source;
      freqDataRef.current = new Uint8Array(analyser.frequencyBinCount);
      analyserAvailableRef.current = true;
    } catch {
      analyserAvailableRef.current = false;
    }
  }, []);

  const getFrequencyData = useCallback(() => {
    if (!analyserAvailableRef.current || !analyserRef.current || !freqDataRef.current) return null;
    analyserRef.current.getByteFrequencyData(freqDataRef.current);
    return freqDataRef.current;
  }, []);

  // Synchronously load + play from a user gesture
  const loadAndPlay = useCallback((track) => {
    const audio = audioRef.current;
    if (!audio || !track?.preview_url) return;
    hasUserGestureRef.current = true;
    initAnalyser();
    if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume().catch(() => {});
    }
    if (audio.src !== track.preview_url) {
      audio.src = track.preview_url;
      audio.load();
    }
    const p = audio.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }, [initAnalyser]);

  // On currentIndex change, load + attempt play (only if we've had a gesture)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const track = queueRef.current[currentIndex];
    if (track?.preview_url) {
      if (audio.src !== track.preview_url) {
        audio.src = track.preview_url;
        audio.load();
      }
      if (hasUserGestureRef.current) {
        const p = audio.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      }
    } else {
      audio.pause();
      audio.removeAttribute("src");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  const play = useCallback(async () => {
    hasUserGestureRef.current = true;
    initAnalyser();
    if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
      await audioCtxRef.current.resume().catch(() => {});
    }
    try { await audioRef.current?.play(); } catch { /* ignored */ }
  }, [initAnalyser]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const toggle = useCallback(() => {
    if (isPlaying) pause();
    else play();
  }, [isPlaying, play, pause]);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, []);

  const seekFraction = useCallback((frac) => {
    const audio = audioRef.current;
    if (audio && audio.duration) {
      audio.currentTime = Math.max(0, Math.min(audio.duration, frac * audio.duration));
    }
  }, []);

  const next = useCallback(() => {
    setCurrentIndex((i) => {
      const q = queueRef.current;
      if (q.length === 0) return -1;
      if (shuffleRef.current && q.length > 1) {
        let r;
        do { r = Math.floor(Math.random() * q.length); } while (r === i);
        return r;
      }
      return (i + 1) % q.length;
    });
  }, []);

  const prev = useCallback(() => {
    const audio = audioRef.current;
    if (audio && audio.currentTime > 3) {
      audio.currentTime = 0;
      return;
    }
    setCurrentIndex((i) => {
      const q = queueRef.current;
      if (q.length === 0) return -1;
      if (shuffleRef.current && q.length > 1) {
        let r;
        do { r = Math.floor(Math.random() * q.length); } while (r === i);
        return r;
      }
      return i <= 0 ? q.length - 1 : i - 1;
    });
  }, []);

  const enqueueAndPlay = useCallback((track) => {
    loadAndPlay(track);
    setQueue((q) => {
      const idx = q.findIndex((t) => t.id === track.id);
      if (idx >= 0) {
        setCurrentIndex(idx);
        return q;
      }
      const nq = [...q, track];
      setCurrentIndex(nq.length - 1);
      return nq;
    });
  }, [loadAndPlay]);

  const addToQueue = useCallback((track) => {
    setQueue((q) => {
      if (q.find((t) => t.id === track.id)) return q;
      const nq = [...q, track];
      if (currentIndexRef.current === -1) {
        setCurrentIndex(0);
      }
      return nq;
    });
  }, []);

  const playFromQueue = useCallback((trackId) => {
    const q = queueRef.current;
    const idx = q.findIndex((t) => t.id === trackId);
    if (idx >= 0) {
      loadAndPlay(q[idx]);
      setCurrentIndex(idx);
    }
  }, [loadAndPlay]);

  const removeFromQueue = useCallback((trackId) => {
    setQueue((q) => {
      const idx = q.findIndex((t) => t.id === trackId);
      if (idx === -1) return q;
      const nq = q.filter((t) => t.id !== trackId);
      const curIdx = currentIndexRef.current;
      if (idx < curIdx) {
        setCurrentIndex(curIdx - 1);
      } else if (idx === curIdx) {
        if (nq.length === 0) {
          setCurrentIndex(-1);
        } else if (curIdx >= nq.length) {
          setCurrentIndex(nq.length - 1);
        }
      }
      return nq;
    });
  }, []);

  const clearQueue = useCallback(() => {
    setQueue([]);
    setCurrentIndex(-1);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute("src");
    }
  }, []);

  const setVolume = useCallback((v) => {
    setVolumeState(Math.max(0, Math.min(1, v)));
  }, []);

  const toggleShuffle = useCallback(() => {
    setShuffle((s) => !s);
  }, []);

  return {
    queue, currentTrack, currentIndex, isPlaying,
    currentTime, duration, volume, shuffle,
    play, pause, toggle, stop, seekFraction,
    next, prev, enqueueAndPlay, addToQueue, playFromQueue,
    removeFromQueue, clearQueue, setVolume, toggleShuffle,
    getFrequencyData,
  };
}
